import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project, onEdit, onDuplicate, onDelete, onView }) => {
  const [showActions, setShowActions] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rendering':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div 
      className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-elevation-2 transition-standard group"
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Project Thumbnail */}
      <div className="relative h-32 bg-muted overflow-hidden">
        <Image
          src={project?.thumbnail}
          alt={project?.thumbnailAlt}
          className="w-full h-full object-cover"
        />
        
        {/* Status Badge */}
        <div className={`absolute top-2 left-2 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project?.status)}`}>
          {project?.status?.charAt(0)?.toUpperCase() + project?.status?.slice(1)}
        </div>

        {/* Quick Actions Overlay */}
        {showActions && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center space-x-2 opacity-0 group-hover:opacity-100 transition-standard">
            <Button
              variant="secondary"
              size="sm"
              iconName="Eye"
              onClick={() => onView(project)}
            >
              View
            </Button>
            <Button
              variant="secondary"
              size="sm"
              iconName="Edit"
              onClick={() => onEdit(project)}
            >
              Edit
            </Button>
          </div>
        )}
      </div>
      {/* Project Details */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground text-sm line-clamp-1">
            {project?.name}
          </h3>
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              iconName="MoreVertical"
              className="opacity-0 group-hover:opacity-100 transition-standard"
              onClick={(e) => {
                e?.stopPropagation();
                setShowActions(!showActions);
              }}
            />
            
            {showActions && (
              <div className="absolute right-0 top-full mt-1 w-32 bg-popover border border-border rounded-lg shadow-elevation-2 z-dropdown">
                <div className="p-1">
                  <button
                    onClick={() => onEdit(project)}
                    className="flex items-center space-x-2 w-full px-2 py-1.5 text-xs text-popover-foreground hover:bg-muted rounded transition-micro"
                  >
                    <Icon name="Edit" size={12} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => onDuplicate(project)}
                    className="flex items-center space-x-2 w-full px-2 py-1.5 text-xs text-popover-foreground hover:bg-muted rounded transition-micro"
                  >
                    <Icon name="Copy" size={12} />
                    <span>Duplicate</span>
                  </button>
                  <button
                    onClick={() => onDelete(project)}
                    className="flex items-center space-x-2 w-full px-2 py-1.5 text-xs text-destructive hover:bg-muted rounded transition-micro"
                  >
                    <Icon name="Trash2" size={12} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="text-xs text-muted-foreground mb-2 line-clamp-2">
          {project?.description}
        </p>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Created {formatDate(project?.createdAt)}</span>
          <div className="flex items-center space-x-1">
            <Icon name="Users" size={12} />
            <span>{project?.creditsCount} credits</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;